import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { Mail, Fingerprint, CalendarDays, ShieldCheck, UserRound, X } from "lucide-react";
import { usePatientAuth } from "../../store/authPatientStore";
import PatientPageHeading from "../../components/organisms/patient-dashboard/PatientPageHeading";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";

function formatDateNaissance(value?: string): string {
  if (!value) return "—";
  const date = parseISO(value);
  if (Number.isNaN(date.getTime())) return "—";
  return format(date, "d MMMM yyyy", { locale: fr });
}

type InfoFieldProps = {
  icon: typeof UserRound;
  label: string;
  value?: string;
  mono?: boolean;
};

function InfoField({ icon: Icon, label, value, mono }: InfoFieldProps) {
  return (
    <div className="flex items-start gap-3 rounded-[10px] border bg-[#FAFBFB] p-3" style={{ borderColor: "#E5E7EB" }}>
      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#DCEFE9] text-[#00553E]">
        <Icon size={16} aria-hidden />
      </div>
      <div className="min-w-0">
        <div className="text-xs text-gray-500">{label}</div>
        <div className={`mt-0.5 truncate font-semibold text-[#0B1F16] ${mono ? "font-mono" : ""}`}>
          {value || "—"}
        </div>
      </div>
    </div>
  );
}

export default function PatientHome() {
  const navigate = useNavigate();
  const location = useLocation();
  const { patient } = usePatientAuth();
  const [showIdentityBanner, setShowIdentityBanner] = useState(false);

  useEffect(() => {
    const state = location.state as { showIdentityBanner?: boolean } | null;
    if (state?.showIdentityBanner) {
      setShowIdentityBanner(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  const fullName = `${patient?.nom ?? ""} ${patient?.prenom ?? ""}`.trim();

  return (
    <div className="space-y-6">
      <PatientPageHeading
        title="Bienvenue dans votre espace patient"
        description={fullName ? `Heureux de vous revoir, ${fullName}.` : "Consultez rapidement vos informations de sante."}
      />

      {showIdentityBanner && (
        <div className="flex items-center justify-between gap-4 rounded-[12px] border bg-white p-4" style={{ borderColor: "#E5E7EB" }}>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#DCEFE9] text-[#00553E]">
              <ShieldCheck size={18} aria-hidden />
            </div>
            <div>
              <p className="font-semibold text-[#0B1F16]">Compte cree avec succes</p>
              <p className="text-sm text-gray-600">Completez votre verification d&apos;identite pour securiser votre compte.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => navigate("/patient/verification-identite")}>
              Verifier mon identite
            </Button>
            <Button size="icon" variant="ghost" aria-label="Fermer" onClick={() => setShowIdentityBanner(false)}>
              <X size={16} />
            </Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <button
          onClick={() => navigate("/patient/dossier")}
          className="rounded-[12px] border bg-white p-5 text-left shadow-sm hover:shadow transition-shadow hover:animate-card-bounce"
          style={{ borderColor: "#E5E7EB" }}
        >
          <p className="text-sm text-gray-500">Dossier</p>
          <p className="mt-2 text-lg font-semibold text-[#0B1F16]">Votre dossier medical</p>
        </button>
        <button
          onClick={() => navigate("/patient/consultations")}
          className="rounded-[12px] border bg-white p-5 text-left shadow-sm hover:shadow transition-shadow hover:animate-card-bounce"
          style={{ borderColor: "#E5E7EB" }}
        >
          <p className="text-sm text-gray-500">Suivi</p>
          <p className="mt-2 text-lg font-semibold text-[#0B1F16]">Vos consultations</p>
        </button>
        <button
          onClick={() => navigate("/patient/ordonnances")}
          className="rounded-[12px] border bg-white p-5 text-left shadow-sm hover:shadow transition-shadow hover:animate-card-bounce"
          style={{ borderColor: "#E5E7EB" }}
        >
          <p className="text-sm text-gray-500">Prescription</p>
          <p className="mt-2 text-lg font-semibold text-[#0B1F16]">Vos ordonnances</p>
        </button>
        <button
          onClick={() => navigate("/patient/vaccinations")}
          className="rounded-[12px] border bg-white p-5 text-left shadow-sm hover:shadow transition-shadow hover:animate-card-bounce"
          style={{ borderColor: "#E5E7EB" }}
        >
          <p className="text-sm text-gray-500">Prevention</p>
          <p className="mt-2 text-lg font-semibold text-[#0B1F16]">Carnet de vaccination</p>
        </button>
      </div>

      <div className="rounded-[12px] border bg-white p-5" style={{ borderColor: "#E5E7EB" }}>
        <p className="text-sm text-gray-600">
          Accedez a vos donnees de sante, consultez vos ordonnances et suivez vos vaccinations depuis une navigation unique.
        </p>
      </div>

      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0 pb-5">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#DCEFE9] font-semibold text-[#00553E]">
              {(patient?.nom?.[0] ?? "").toUpperCase()}
              {(patient?.prenom?.[0] ?? "").toUpperCase()}
            </div>
            <div>
              <CardTitle>Informations generales</CardTitle>
              <CardDescription>Renseignees lors de la creation de votre compte</CardDescription>
            </div>
          </div>
          {patient?.statut && <Badge variant="soft">{patient.statut}</Badge>}
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <InfoField icon={UserRound} label="Nom" value={patient?.nom} />
            <InfoField icon={UserRound} label="Prenom" value={patient?.prenom} />
            <InfoField icon={Mail} label="Email" value={patient?.email} />
            <InfoField icon={Fingerprint} label="NIP" value={patient?.nip} mono />
            <InfoField icon={CalendarDays} label="Date de naissance" value={formatDateNaissance(patient?.dateNaissance)} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

